import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Box,
  Stack,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  CircularProgress,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ConfirmationDialog from "../../dialogs/ConfirmationDialog";
import {
  deletePetition,
  editPetitionTitle,
  getPetitionTitles,
  togglePetitionPin,
} from "../../actions/petitionActions";
import { showInsufficientCreditsModal } from "../../actions/creditActions";
import { deductCredits } from "../../utils/HelperFunction/deductCredits";
import {
  Plus,
  Search,
  ChevronLeft,
  Menu as MenuIcon,
  Edit,
  Trash2,
  Check,
  X,
  MoreHorizontal,
  ChevronDown,
  ChevronRight,
  FileText,
  History,
  Pin,
  PinOff,
} from "lucide-react";
import LogoLoader from "../UI/LogoLoader";

const Sidebar = ({
  isCollapsed = false,
  toggleSidebar,
  setIsHistoryClicked,
  setPetitionToEdit,
  setGeneratedPetition,
  setReadOnly,
}) => {
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const { documentId } = useParams();
  const dispatch = useDispatch();
  const credits = useSelector((state) => state.credit.credits);
  const userId = useSelector((state) => state.credit.userId);
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const petitionTitles = useSelector((state) => state.petitionTitles);
  const { loading, titles } = petitionTitles;

  const [selectedChat, setSelectedChat] = useState({ chatId: "", title: "" });
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [newTitle, setNewTitle] = useState("");

  const [isEditing, setIsEditing] = useState({ chatId: "" });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const filteredData = titles && titles?.filter((item) => item.Title !== null);

  const groupedData =
    filteredData && Object.groupBy(filteredData, (title) => title?.Category);

  const open = Boolean(anchorEl);

  const inputRef = useRef(null);

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleNewChatClick = () => {
    setSelectedChat({ chatId: "", title: "" });
    setExpanded(null);
    setIsHistoryClicked(false);
    setReadOnly(false);
    setPetitionToEdit({});
    dispatch({ type: "NEW_PETITION_REQUEST" });
    dispatch({ type: "SELECTED_PETITION_RESET" });
    isMobileView && toggleSidebar();
  };

  const handleInputChange = (e) => {
    const cursorPosition = inputRef.current.selectionStart;
    setNewTitle(e.target.value);

    // Set the cursor back to where it was
    setTimeout(() => {
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }, 0);
  };

  const handleSaveEdit = (chatId) => {
    if (!newTitle) return;
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal());
      return;
    }
    dispatch(editPetitionTitle(userInfo?._id, chatId, newTitle));
    // Reset editing state after saving
    setIsEditing({ chatId: "" });
    setNewTitle("");
    // Deduct credits
    const deductionSuccess = deductCredits(
      {
        userId,
        creditsToDeduct: 1,
        event: "Petition title edit on petition drafting page.",
      },
      dispatch
    );

    if (!deductionSuccess) {
      console.warn(
        "Failed to deduct credits. Ensure retry or user notification."
      );
    }
  };

  const handleCancelEdit = (index) => {
    setIsEditing({ chatId: "" });
    setNewTitle("");
  };

  const selectPetition = (petition) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal());
      return;
    }
    setIsEditing({ chatId: "" });
    setSelectedChat({ chatId: petition.chat_id, title: petition.Title });
    setAnchorEl(null);
    setReadOnly(true);
    setGeneratedPetition("");
    dispatch({ type: "SET_SELECTED_PETITION", payload: petition });
    setIsHistoryClicked(false);
    setPetitionToEdit({});
    // Deduct credits
    const deductionSuccess = deductCredits(
      {
        userId,
        creditsToDeduct: 1,
        event: "Petition preview on petition drafting page.",
      },
      dispatch
    );

    if (!deductionSuccess) {
      console.warn(
        "Failed to deduct credits. Ensure retry or user notification."
      );
    }
  };

  const handleListItemClick = (petition) => {
    selectPetition(petition);
    isMobileView && toggleSidebar();
  };

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditTitle = (event, chatId) => {
    event.stopPropagation();
    setIsEditing({ chatId });
    const findChat = titles?.find((title) => title.chat_id === chatId);
    setNewTitle(findChat.Title);
  };

  const handleTogglePin = (event, chatId) => {
    event.stopPropagation();
    const findChat = titles?.find((title) => title.chat_id === chatId);
    dispatch(togglePetitionPin(userInfo?._id, chatId, !findChat?.isPinned));
    setAnchorEl(null);
  };

  const handleConfirmDelete = (chatId) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal());
      return;
    }
    dispatch(deletePetition(userInfo?._id, chatId));
    setSelectedChat({ chatId: "", title: "" });
    dispatch({ type: "SELECTED_PETITION_RESET" });
    setOpenDeleteDialog(false);
    // Deduct credits
    const deductionSuccess = deductCredits(
      {
        userId,
        creditsToDeduct: 1,
        event: "Delete petition on petition drafting page.",
      },
      dispatch
    );

    if (!deductionSuccess) {
      console.warn(
        "Failed to deduct credits. Ensure retry or user notification."
      );
    }
  };

  const handleDeleteChat = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(null);
    // Open delete dialog
    setOpenDeleteDialog(true);
  };

  const handleAccordionToggle = (category) => {
    setExpanded((prev) => {
      if (prev === category) {
        setSelectedChat({ chatId: "", title: "" });
        dispatch({ type: "SELECTED_PETITION_RESET" });
        return null;
      } else {
        return category;
      }
    });
  };

  useEffect(() => {
    dispatch(getPetitionTitles('petition', userInfo?._id));
    return () => {
      dispatch({ type: "SELECTED_PETITION_RESET" });
    };
  }, []);

  const hasHandledDeepLink = useRef(false);

  useEffect(() => {
    if (!documentId || !titles?.length || hasHandledDeepLink.current) return;
    const match = titles.find((item) => item.chat_id === documentId);
    if (match) {
      hasHandledDeepLink.current = true;
      setExpanded(match.Category);
      selectPetition(match);
    }
  }, [documentId, titles]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        ...(isMobileView
          ? { width: "320px" }
          : {
              flexBasis: isCollapsed ? "60px" : "320px",
              flexShrink: 0,
              maxWidth: isCollapsed ? "60px" : "320px",
              transition: "flex-basis 0.3s ease",
            }),
        height: "100%",
        boxSizing: "border-box",
        margin: "12px",
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        border: "1px solid #F1F5F9",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
        overflow: "hidden",
                fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          padding: "20px 24px",
          borderBottom: "1px solid #F1F5F9",
          backgroundColor: "#FFFFFF",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <IconButton
              size="small"
              sx={{
                display: { xs: "flex", lg: "none" },
                width: "40px",
                height: "40px",
                borderRadius: "10px",
                backgroundColor: "#F0F9FF",
                color: "#0073A6",
                "&:hover": {
                  backgroundColor: "#E0F2FE",
                },
              }}
              onClick={toggleSidebar}
            >
              <MenuIcon size={20} />
            </IconButton>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                backgroundColor: "#F0F9FF",
                borderRadius: "12px",
                color: "#0073A6",
              }}
            >
              <FileText size={20} />
            </Box>
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#12243C",
                fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
              }}
            >
              Petitions
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Tooltip title="New Petition">
              <IconButton
                size="small"
                onClick={handleNewChatClick}
                sx={{
                  width: "36px",
                  height: "36px",
                  backgroundColor: "#F8FAFC",
                  color: "#0073A6",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: "#F0F9FF",
                  },
                }}
              >
                <Plus size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title="View History">
              <IconButton
                size="small"
                onClick={() => {
                  setIsHistoryClicked(true);
                  setSelectedChat({ chatId: "", title: "" });
                  setExpanded(null);
                  isMobileView && toggleSidebar();
                }}
                sx={{
                  width: "36px",
                  height: "36px",
                  backgroundColor: "#F8FAFC",
                  color: "#64748B",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: "#F1F5F9",
                  },
                }}
              >
                <History size={16} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: "16px 0",
          "::-webkit-scrollbar": {
            width: "6px",
          },
          "::-webkit-scrollbar-track": {
            background: "#F8FAFC",
          },
          "::-webkit-scrollbar-thumb": {
            background: "#CBD5E1",
            borderRadius: "8px",
            "&:hover": {
              background: "#94A3B8",
            },
          },
        }}
      >
        {!isMobileView && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignContent: "center",
              alignItems: "center",
              mb: 1,
            }}
          >

          </Box>
        )}


        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "40px 20px",
            }}
          >
            <LogoLoader size={isMobileView ? "sm" : "md"} duration="1s" />
          </Box>
        ) : titles?.length > 0 && !isCollapsed ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              padding: "0 16px",
            }}
          >
            {Object.entries(groupedData)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([category, items]) => (
                <Accordion
                  key={category}
                  expanded={expanded === category}
                  onChange={() => handleAccordionToggle(category)}
                  sx={{
                    margin: "8px 0",
                    borderRadius: "12px",
                    boxShadow: "none",
                    border: "1px solid #F1F5F9",
                    "&:before": { display: "none" },
                    "&.Mui-expanded": {
                      margin: "8px 0",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ChevronDown size={16} color="#64748B" />}
                    aria-controls={`${category}-content`}
                    id={`${category}-header`}
                    sx={{
                      width: "100%",
                      overflow: "hidden",
                      backgroundColor: "#F8FAFC",
                      borderRadius: "12px",
                      padding: "12px 16px",
                      minHeight: "48px",
                      "&.Mui-expanded": {
                        minHeight: "48px",
                        padding: "12px 16px",
                      },
                      ".MuiAccordionSummary-content": {
                        margin: "0px",
                        "&.Mui-expanded": {
                          margin: "0px",
                        },
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#475569",
                        fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                      }}
                    >
                      {category} ({items?.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      padding: "8px 0",
                      backgroundColor: "#FFFFFF",
                      maxHeight: "300px",
                      overflowY: "auto",
                      "::-webkit-scrollbar": {
                        width: "4px",
                      },
                      "::-webkit-scrollbar-track": {
                        background: "#F8FAFC",
                      },
                      "::-webkit-scrollbar-thumb": {
                        background: "#CBD5E1",
                        borderRadius: "4px",
                        "&:hover": {
                          background: "#94A3B8",
                        },
                      },
                    }}
                  >
                    {[...(items || [])]
                      .sort((a, b) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0))
                      .map((item, index) => (
                      <Box key={item.chat_id}>
                        <ListItem disablePadding>
                          <ListItemButton
                            disableRipple
                            sx={{
                              width: "100%",
                              marginY: "2px",
                              borderRadius: "8px",
                              height: "40px",
                              padding: "8px 12px",
                              "&.Mui-selected": {
                                backgroundColor: "#F0F9FF",
                                "&:hover": {
                                  backgroundColor: "#F0F9FF",
                                },
                              },
                              "&:hover": {
                                backgroundColor: "#F8FAFC",
                              },
                            }}
                            selected={selectedChat.chatId === item.chat_id}
                            onClick={() => handleListItemClick(item)}
                          >
                            {isEditing.chatId === item.chat_id ? (
                              <Stack direction="row" width="100%" alignItems="center" gap={1}>
                                <input
                                  ref={inputRef}
                                  style={{
                                    width: "100%",
                                    fontSize: "14px",
                                    border: "none",
                                    outline: "none",
                                    backgroundColor: "transparent",
                                    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                  }}
                                  type="text"
                                  autoFocus
                                  value={newTitle}
                                  onClick={(e) => e.stopPropagation()}
                                  onFocus={(e) => e.stopPropagation()}
                                  onChange={handleInputChange}
                                />
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSaveEdit(item.chat_id);
                                    setAnchorEl(null);
                                  }}
                                  sx={{
                                    width: "24px",
                                    height: "24px",
                                    color: "#10B981",
                                  }}
                                >
                                  <Check size={14} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancelEdit();
                                    setAnchorEl(null);
                                  }}
                                  sx={{
                                    width: "24px",
                                    height: "24px",
                                    color: "#EF4444",
                                  }}
                                >
                                  <X size={14} />
                                </IconButton>
                              </Stack>
                            ) : (
                              <Stack
                                direction="row"
                                width="100%"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Stack direction="row" alignItems="center" gap="6px" flex={1} minWidth={0}>
                                  <Typography
                                    variant="subtitle2"
                                    noWrap
                                    sx={{
                                      textOverflow: "ellipsis",
                                      overflow: "hidden",
                                      whiteSpace: "nowrap",
                                      fontSize: "14px",
                                      color: selectedChat.chatId === item.chat_id ? "#0073A6" : "#475569",
                                      fontWeight: selectedChat.chatId === item.chat_id ? "500" : "400",
                                      fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                    }}
                                  >
                                    {item.Title}
                                  </Typography>
                                  {item.isPinned && (
                                    <Pin size={12} color="#0073A6" style={{ flexShrink: 0 }} />
                                  )}
                                </Stack>
                                {selectedChat.chatId === item.chat_id && (
                                  <>
                                    <IconButton
                                      size="small"
                                      id="basic-button"
                                      aria-controls={
                                        open ? "basic-menu" : undefined
                                      }
                                      aria-haspopup="true"
                                      aria-expanded={open ? "true" : undefined}
                                      onClick={handleMenuOpen}
                                      sx={{
                                        width: "24px",
                                        height: "24px",
                                        color: "#64748B",
                                      }}
                                    >
                                      <MoreHorizontal size={14} />
                                    </IconButton>
                                    <Menu
                                      anchorEl={anchorEl}
                                      id="basic-menu"
                                      open={open}
                                      onClose={handleMenuClose}
                                      PaperProps={{
                                        sx: {
                                          borderRadius: "8px",
                                          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                                          border: "1px solid #F1F5F9",
                                        },
                                      }}
                                    >
                                      <MenuItem
                                        sx={{
                                          fontSize: "14px",
                                          fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                        }}
                                        onClick={(e) =>
                                          handleTogglePin(e, item.chat_id)
                                        }
                                      >
                                        {item.isPinned ? (
                                          <PinOff size={14} style={{ marginRight: "8px" }} />
                                        ) : (
                                          <Pin size={14} style={{ marginRight: "8px" }} />
                                        )}
                                        {item.isPinned ? "Unpin" : "Pin"}
                                      </MenuItem>
                                      <MenuItem
                                        sx={{
                                          fontSize: "14px",
                                          fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                        }}
                                        onClick={(e) =>
                                          handleEditTitle(e, item.chat_id)
                                        }
                                      >
                                        <Edit size={14} style={{ marginRight: "8px" }} />
                                        Edit
                                      </MenuItem>
                                      <MenuItem
                                        sx={{
                                          fontSize: "14px",
                                          color: "#EF4444",
                                          fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                        }}
                                        onClick={(e) => handleDeleteChat(e)}
                                      >
                                        <Trash2 size={14} style={{ marginRight: "8px" }} />
                                        Delete
                                      </MenuItem>
                                    </Menu>
                                  </>
                                )}
                              </Stack>
                            )}
                          </ListItemButton>
                        </ListItem>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              padding: "40px 20px",
            }}
          >
            {!isCollapsed && (
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: "14px",
                  color: "#64748B",
                  textAlign: "center",
                  fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                Get started with your legal drafting.
                <br />
                Create a new petition today! 🚀
              </Typography>
            )}
          </Box>
        )}
      </Box>
      <ConfirmationDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        title="Delete Petition"
        description={`This will delete "${selectedChat?.title}". You won't be able to recover it.`}
        onConfirm={() => handleConfirmDelete(selectedChat?.chatId)}
        confirmText="Delete"
        confirmColor="error"
      />
    </Box>
  );
};

export default Sidebar;
